import { useState, useEffect } from 'react';
import apiClient from '../services/apiClient';
import { useAuth } from '../contexts/AuthContext';
import socketService from '../services/socketService';

export const useBillingHistory = (limit = 10, autoRefresh = true) => {
  const [billingData, setBillingData] = useState({
    payments: [],
    invoices: [],
    summary: null
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const { user, isAuthenticated } = useAuth();

  const fetchBillingHistory = async (offset = 0, resetData = true) => {
    if (!isAuthenticated || !user) {
      setBillingData({ payments: [], invoices: [], summary: null });
      setLoading(false);
      return;
    }

    try {
      if (resetData) setLoading(true);

      const response = await apiClient.get(`/api/subscription/billing-history?limit=${limit}&offset=${offset}`);
      
      if (response.success && response.data) {
        const newData = response.data;
        
        setBillingData(prevData => {
          if (resetData || offset === 0) {
            // Replace data (fresh fetch)
            return {
              payments: newData.payments || [],
              invoices: newData.invoices || [],
              summary: newData.summary || null
            };
          } else {
            // Append data (pagination)
            return {
              payments: [...prevData.payments, ...(newData.payments || [])],
              invoices: [...prevData.invoices, ...(newData.invoices || [])],
              summary: newData.summary || prevData.summary
            };
          }
        });
        
        setHasMore(newData.hasMore || false);
      } else {
        setBillingData({ payments: [], invoices: [], summary: null });
        setHasMore(false);
      }
      setError(null);
    } catch (err) {
      setError(err.message);
      if (resetData) {
        setBillingData({ payments: [], invoices: [], summary: null });
        setHasMore(false);
      }
    } finally {
      setLoading(false);
    }
  };

  // Initial fetch
  useEffect(() => {
    fetchBillingHistory();
  }, [isAuthenticated, user, limit]);

  // Setup real-time updates for billing history
  useEffect(() => {
    if (!isAuthenticated || !user || !autoRefresh) return;

    const handlePaymentUpdate = (data) => {
      if (data.type === 'payment_completed') {
        // Refresh billing history when new payment is completed
        setTimeout(() => fetchBillingHistory(), 1000);
      }
    };

    const handleSubscriptionUpdate = (data) => {
      if (data.type === 'subscription_created' || data.type === 'subscription_updated') {
        // Refresh billing history when subscription changes (might affect invoices)
        setTimeout(() => fetchBillingHistory(), 1000);
      }
    };

    // Connect to socket if not connected
    socketService.connect().then(() => {
      // Subscribe to real-time events
      const unsubscribePayment = socketService.on('payment_updated', handlePaymentUpdate);
      const unsubscribeSubscription = socketService.on('subscription_updated', handleSubscriptionUpdate);

      // Return cleanup function
      return () => {
        unsubscribePayment();
        unsubscribeSubscription();
      };
    }).catch(() => {
      // Socket connection failed for billing updates
    });

  }, [isAuthenticated, user, autoRefresh]);

  // Load more function for pagination
  const loadMore = async () => {
    if (!hasMore || loading) return;
    
    const currentOffset = billingData.payments.length;
    await fetchBillingHistory(currentOffset, false);
  };

  // Refresh function
  const refresh = async () => {
    await fetchBillingHistory(0, true);
  };

  // Helper functions
  const getTotalSpent = () => {
    return billingData.summary?.totalAmount || 
           billingData.payments.reduce((sum, payment) => 
             sum + (payment.status === 'succeeded' ? (payment.amount || 0) : 0), 0
           );
  };

  const getSuccessfulPayments = () => {
    return billingData.payments.filter(payment => payment.status === 'succeeded');
  };

  const getPendingPayments = () => {
    return billingData.payments.filter(payment => payment.status === 'pending');
  };

  const getFailedPayments = () => {
    return billingData.payments.filter(payment => payment.status === 'failed');
  };

  const getInvoiceByPaymentId = (paymentId) => {
    return billingData.payments.find(payment => payment.id === paymentId)?.invoice || null;
  };

  // Download invoice/receipt
  const downloadInvoice = async (payment) => {
    try {
      const invoiceUrl = payment.invoice?.receipt_url || 
                        payment.transactionDetails?.receipt_url ||
                        payment.invoice?.pdf_url;
      
      if (invoiceUrl) {
        window.open(invoiceUrl, '_blank');
        return true;
      } else {
        return false;
      }
    } catch (error) {
      return false;
    }
  };

  return {
    // Data
    payments: billingData.payments,
    invoices: billingData.invoices,
    summary: billingData.summary,
    
    // State
    loading,
    error,
    hasMore,
    
    // Actions
    refresh,
    loadMore,
    downloadInvoice,
    
    // Helper functions
    getTotalSpent,
    getSuccessfulPayments,
    getPendingPayments,
    getFailedPayments,
    getInvoiceByPaymentId,
    
    // Raw fetch function for custom use
    fetchBillingHistory
  };
};

export default useBillingHistory;